import React, { useState } from 'react';

const DEFAULT_CHECKLIST = [
  { id: 'arrival', label: '🚚 Arrival & Equipment Unloaded at Venue', desc: 'Arrived at least 45 mins prior to start time' },
  { id: 'assembly', label: '🔌 Booth Hardware Assembly & Power Check', desc: 'Secure cables with gaffer tape, verify stable outlet' },
  { id: 'camera', label: '💡 Camera, Lighting & Touchscreen Calibration', desc: 'Test flash, camera focus, and touch responsiveness' },
  { id: 'test_print', label: '🖨️ Printer Media Loaded & Test Print Passed', desc: 'Ensure ink ribbon & 4×6 / 2×6 photo paper are fresh' },
  { id: 'backdrop', label: '🖼️ Backdrop Erected & Props Station Prepared', desc: 'Ensure backdrop is wrinkle-free and sanitized props are set' },
  { id: 'client_signoff', label: '🤝 Client Host Introduction & Position Sign-Off', desc: 'Show sample print to host and confirm satisfaction' },
];

export default function LiveEventView({
  event,
  allEvents,
  onSelectEvent,
  onStatusChange,
  onOpenCompleteModal,
}) {
  const [checklist, setChecklist] = useState(() => {
    const saved = localStorage.getItem(`pixly_checklist_${event?.id}`);
    return saved ? JSON.parse(saved) : {};
  });

  if (!event) {
    const activeCandidates = allEvents.filter((e) =>
      ['accepted', 'in_progress'].includes(e.assignment_status)
    );

    return (
      <div
        style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: 16,
          border: '1px dashed rgba(255,255,255,0.1)',
        }}
      >
        <span style={{ fontSize: 44, display: 'block', marginBottom: 12 }}>⚡</span>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#fff' }}>No Active Event Selected</h2>
        <p style={{ margin: '0 0 20px', color: '#a0a0b8', fontSize: 13, maxWidth: 400, marginInline: 'auto' }}>
          Select an accepted or in-progress event to view the live execution workflow and day-of checklist.
        </p>

        {activeCandidates.length > 0 && (
          <div style={{ maxWidth: 440, margin: '0 auto', textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#9F5CFF', marginBottom: 8, textTransform: 'uppercase' }}>
              Select Active Event:
            </div>
            {activeCandidates.map((c) => (
              <button
                key={c.id}
                onClick={() => onSelectEvent(c)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(159,92,255,0.08)',
                  border: '1px solid rgba(159,92,255,0.25)',
                  borderRadius: 10,
                  color: '#fff',
                  marginBottom: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    📅 {c.event_date} • {c.venue_name || 'Venue'}
                  </div>
                  <div style={{ fontSize: 11, color: '#a0a0b8' }}>
                    🕒 {c.start_time} - {c.end_time} ({c.booth_type})
                  </div>
                </div>
                <span style={{ fontSize: 12, color: '#9F5CFF', fontWeight: 700 }}>Open →</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const toggleCheck = (id) => {
    const updated = { ...checklist, [id]: !checklist[id] };
    setChecklist(updated);
    try {
      localStorage.setItem(`pixly_checklist_${event.id}`, JSON.stringify(updated));
    } catch (_) {}
  };

  const completedChecks = Object.values(checklist).filter(Boolean).length;
  const progressPercent = Math.round((completedChecks / DEFAULT_CHECKLIST.length) * 100);

  const isInProgress = event.assignment_status === 'in_progress';
  const isCompleted = event.assignment_status === 'completed';

  let extras = event.booking_extras;
  if (typeof extras === 'string') {
    try {
      extras = JSON.parse(extras);
    } catch (_) {
      extras = [];
    }
  }
  if (!Array.isArray(extras)) extras = [];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Event Header Banner */}
      <div
        style={{
          background: isInProgress
            ? 'linear-gradient(135deg, rgba(159,92,255,0.2) 0%, rgba(16,20,40,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(0,210,255,0.15) 0%, rgba(16,20,40,0.95) 100%)',
          borderRadius: 20,
          border: isInProgress
            ? '1px solid rgba(159, 92, 255, 0.4)'
            : '1px solid rgba(0, 210, 255, 0.3)',
          padding: '24px',
          marginBottom: 20,
          boxShadow: '0 15px 35px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span
                style={{
                  background: isInProgress ? '#9F5CFF' : '#00D2FF',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                  padding: '3px 10px',
                  borderRadius: 20,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {isInProgress ? '🔴 LIVE EVENT IN PROGRESS' : 'READY FOR CHECK-IN'}
              </span>
              <span style={{ fontSize: 12, color: '#a0a0b8' }}>BKG-{String(event.id).padStart(5, '0')}</span>
            </div>

            <h1 style={{ margin: '0 0 4px', fontSize: 20, color: '#fff', fontWeight: 800 }}>
              {event.venue_name || 'Event Venue'}
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: '#a0a0b8' }}>
              📍 {event.venue_address || 'No venue address specified'}
            </p>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>📅 {event.event_date}</div>
            <div style={{ fontSize: 13, color: '#00D2FF', fontWeight: 600 }}>
              🕒 {event.start_time} - {event.end_time}
            </div>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          {!isInProgress && !isCompleted && (
            <button
              onClick={() => onStatusChange(event.id, 'in_progress')}
              style={{
                flex: 1,
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #00D2FF 0%, #0077ff 100%)',
                border: 'none',
                borderRadius: 10,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              ▶️ Check In / Start Live Event
            </button>
          )}

          {isInProgress && (
            <button
              onClick={() => onOpenCompleteModal(event)}
              style={{
                flex: 1,
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #27C85A 0%, #1fab47 100%)',
                border: 'none',
                borderRadius: 10,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              🏁 Wrap Up & Mark Completed
            </button>
          )}

          {(event.phone_number || event.customer?.phone_number) && (
            <a
              href={`tel:${event.phone_number || event.customer?.phone_number}`}
              style={{
                padding: '12px 18px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                color: '#27C85A',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              📞 Call Host
            </a>
          )}
        </div>
      </div>

      {/* Checklist Section */}
      <div
        style={{
          background: 'linear-gradient(145deg, #101428 0%, #0d0f22 100%)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          padding: '24px',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>
              📋 Day-of-Event Execution Checklist
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#7c7c9c' }}>
              Standard operating protocol for on-site Pixly Booth hosts
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: progressPercent === 100 ? '#27C85A' : '#9F5CFF' }}>
              {completedChecks} / {DEFAULT_CHECKLIST.length} Done ({progressPercent}%)
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: progressPercent === 100 ? '#27C85A' : 'linear-gradient(90deg, #9F5CFF, #00D2FF)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* Checklist Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {DEFAULT_CHECKLIST.map((item) => {
            const isChecked = Boolean(checklist[item.id]);
            return (
              <div
                key={item.id}
                onClick={() => toggleCheck(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 16px',
                  borderRadius: 12,
                  background: isChecked ? 'rgba(39, 200, 90, 0.08)' : 'rgba(255,255,255,0.02)',
                  border: isChecked ? '1px solid rgba(39, 200, 90, 0.3)' : '1px solid rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    marginTop: 2,
                    background: isChecked ? '#27C85A' : 'transparent',
                    border: isChecked ? 'none' : '2px solid rgba(255,255,255,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  {isChecked && '✓'}
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isChecked ? '#27C85A' : '#fff',
                      textDecoration: isChecked ? 'line-through' : 'none',
                    }}
                  >
                    {item.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mid-Event Extras Box */}
      {extras.length > 0 && (
        <div
          style={{
            background: 'linear-gradient(145deg, #181938 0%, #101228 100%)',
            borderRadius: 16,
            border: '1px solid rgba(245, 166, 35, 0.3)',
            padding: '20px',
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 18 }}>⭐</span>
            <h3 style={{ margin: 0, fontSize: 15, color: '#f5a623', fontWeight: 700 }}>
              Mid-Event Extras (Authorized by Admin)
            </h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {extras.map((ex, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{ex.name}</div>
                <div style={{ fontSize: 11, color: '#a0a0b8' }}>
                  Qty: {ex.quantity || 1} • Rate: ${parseFloat(ex.unit_price || 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
